import { Controller } from "@hotwired/stimulus"
import { categoryIndex } from "./threat_chart/helper"

export default class extends Controller {
  static values = {
    data: Object,
    showAllYears: { type: Boolean, default: true },
  }

  static targets = [
    "chart",
    "toggleAllYearsButton",
    "toggleAllYearsLabel",
    "toggleAllYearsChevron",
  ]

  updateDateCategories(dateCategories, category) {
    let low = 0
    let high = dateCategories.length

    while (low < high) {
      const mid = Math.floor((low + high) / 2)

      if (dateCategories[mid] < category) {
        low = mid + 1
      } else if (dateCategories[mid] == category) {
        return dateCategories
      } else {
        high = mid
      }
    }

    dateCategories.splice(low, 0, category)

    return dateCategories
  }

  connect() {
    const datesCategories = {}

    const { history, predictions } = this.dataValue
    const byCategory = history ? history.by_category : {}

    Object.keys(byCategory).forEach((key) => {
      const items = byCategory[key]

      items.map((item) => {
        const start = item.start_at
        const end = item.end_at
        const startDate = new Date(start + "Z")
        const endDate = new Date(end + "Z")

        const day = 24 * 60 * 60 * 1000
        let nextDate = startDate

        while (nextDate.getTime() <= endDate.getTime()) {
          const dateString = nextDate.toISOString().split("T")[0]

          datesCategories[dateString] = this.updateDateCategories(
            datesCategories[dateString] || [],
            categoryIndex(key),
          )

          nextDate = new Date(nextDate.getTime() + day)
        }
      })
    })

    const currentYear = new Date().getFullYear()
    let minYear = history
      ? parseInt(history.first_seen_at.split("-")[0])
      : currentYear
    const predictionYears = new Set()

    if (predictions) {
      predictions.forEach((p) => {
        const predictedYear = parseInt(p.predicted_at.split("-")[0])
        minYear = minYear < predictedYear ? minYear : predictedYear
        predictionYears.add(predictedYear)

        const dateString = p.predicted_at.split("T")[0]

        datesCategories[dateString] = this.updateDateCategories(
          datesCategories[dateString] || [],
          categoryIndex("prediction"),
        )
      })
    }

    this.currentYear = currentYear
    this.minYear = minYear
    this.datesCategories = datesCategories
    this.predictionYears = predictionYears

    this.render()
  }

  toggleAllYearsShow() {
    this.showAllYearsValue = !this.showAllYearsValue
    this.toggleAllYearsLabelTarget.textContent = this.showAllYearsValue
      ? "Show less"
      : "Show more"
    this.toggleAllYearsChevronTarget.classList.toggle(
      "rotate-180",
      this.showAllYearsValue,
    )
    this.render()
  }

  render() {
    this.renderCalendars()
    this.toggleAllYearsButtonTarget.hidden = this.minYear >= this.currentYear
  }

  renderCalendars() {
    let calendars = []

    if (this.showAllYearsValue) {
      let year = this.currentYear

      while (year >= this.minYear) {
        const expanded =
          year === this.currentYear || this.predictionYears.has(year)
        calendars.push(this.calendar(year, expanded))
        year--
      }
    } else {
      calendars.push(this.calendar(this.currentYear, true))
    }

    const calendarsContainer = document.createElement("div")
    calendarsContainer.setAttribute(
      "data-controller",
      "ioc-threat-chart-calendar",
    )
    calendarsContainer.setAttribute(
      "data-ioc-threat-chart-calendar-dates-categories-value",
      JSON.stringify(this.datesCategories),
    )

    calendars.forEach((calendar) => {
      calendarsContainer.appendChild(calendar)
    })

    this.chartTarget.replaceChildren(calendarsContainer)
  }

  calendar(year, expanded = true) {
    const calendarContainer = document.createElement("div")
    calendarContainer.setAttribute("class", "mb-4")

    const header = document.createElement("div")
    header.setAttribute(
      "class",
      "flex items-center gap-2 cursor-pointer select-none py-1",
    )

    const title = document.createElement("p")
    title.setAttribute(
      "class",
      "mb-0 text-sm font-semibold tracking-wide text-white/85",
    )
    title.innerHTML = `${year}`

    const chevron = document.createElement("span")
    chevron.setAttribute(
      "class",
      "inline-flex text-white/70 transition-transform duration-200",
    )
    chevron.innerHTML =
      '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg>'

    chevron.classList.toggle("rotate-180", expanded)
    header.appendChild(title)
    header.appendChild(chevron)

    const chartContainer = document.createElement("div")
    chartContainer.setAttribute("class", "overflow-auto")

    if (!expanded) {
      chartContainer.hidden = true
    }

    const chart = document.createElement("div")
    chart.setAttribute("style", "min-width: 450px; height: 150px;")
    chart.setAttribute("data-ioc-threat-chart-calendar-target", "chart")
    chart.setAttribute("data-year", year)

    header.addEventListener("click", () => {
      const isHidden = chartContainer.hidden
      chartContainer.hidden = !isHidden

      if (isHidden) {
        chevron.classList.add("rotate-180")
        chart.dispatchEvent(new CustomEvent("calendar:resize"))
      } else {
        chevron.classList.remove("rotate-180")
      }
    })

    chartContainer.appendChild(chart)

    calendarContainer.appendChild(header)
    calendarContainer.appendChild(chartContainer)

    return calendarContainer
  }
}
